// ticker-incidents.js — shaping compartido del cintillo de incidentes.
// Convierte incidentes crudos (ya enriquecidos con categoryData) en los items
// que consume IncidentTicker.vue. Único origen de verdad para el mapa
// (MapV2.tickerIncidents) y para Analytics Monitor, garantizando cintillos
// idénticos. Función PURA: sin estado, sin acceso al DOM.

import { getIncidentMdiIcon, getIncidentCategoryLabel, getIncidentColorVar } from './incident-category-resolver'
import { timeAgo } from '../utils/map-constants'

// Máximo de items que el cintillo muestra (igual que MapV2.tickerIncidents).
var MAX_TICKER_ITEMS = 25

// Prioridad → rank de orden (ALTA primero).
function rankOf(p) {
  var u = (p || '').toUpperCase()
  if (u === 'ALTA' || u === 'HIGH') return 0
  if (u === 'MEDIA' || u === 'MEDIUM') return 1
  if (u === 'BAJA' || u === 'LOW') return 2
  return 3
}

// Prioridad → color (mismos tokens de tema que el sidebar).
function colorOf(p) {
  var u = (p || '').toUpperCase()
  if (u === 'ALTA' || u === 'HIGH') return 'var(--kln-alert-danger)'
  if (u === 'MEDIA' || u === 'MEDIUM') return 'var(--kln-alert-warning)'
  if (u === 'BAJA' || u === 'LOW') return 'var(--kln-alert-success)'
  return 'var(--kln-text-muted)'
}

function timeMs(ts) {
  var ms = ts ? new Date(ts).getTime() : 0
  return isNaN(ms) ? 0 : ms
}

/**
 * @param {Array<Object>} rawIncidents Incidentes crudos. Para que la categoría
 *   resuelva correctamente deben venir enriquecidos con `categoryData`
 *   (igual que hace el initial-loader del mapa).
 * @returns {Array<Object>} items para IncidentTicker (máx 25, ordenados).
 */
export function shapeTickerIncidents(rawIncidents) {
  var src = rawIncidents || []
  var seen = {}
  var out = []
  for (var i = 0; i < src.length; i++) {
    var inc = src[i]
    if (!inc || inc.status !== 'ACTIVO') continue
    var id = (inc.id != null) ? inc.id : inc._id
    if (id == null) continue
    var monitorId = 'incident-' + id
    if (seen[monitorId]) continue
    seen[monitorId] = 1

    var attrs = inc.attributes || {}
    // `shaped` reproduce EXACTAMENTE el objeto alert.data que MapV2 arma en
    // _generateAlertsFromEntities (id/priority/address/category/raw). Los
    // helpers de categoría (getIncident*) reciben este shape — NO el incidente
    // crudo `inc` — a propósito: el cintillo del mapa resuelve la categoría
    // desde alert.data (sin categoryData top-level), y pasar `shaped` garantiza
    // que Analytics resuelva idéntico. Pasar `inc` activaría otra rama del
    // resolver y rompería la paridad byte-a-byte con el mapa.
    var shaped = {
      id: id,
      priority: attrs.prioridad || inc.priority || '',
      address: attrs.municipio || inc.address || '',
      category: inc.category || (inc.categoryData && inc.categoryData.name) || '',
      raw: inc,
    }
    // tipo = mismas expresiones que _generateAlertsFromEntities (subtitle||title).
    var subtitle = inc.tipoIncidente || inc.description || (inc.categoryData && inc.categoryData.name) || ''
    var title = inc.name || inc.uniqueId || String(id)
    var lugar = shaped.address
    if (String(lugar).trim().toUpperCase() === 'N/A') lugar = ''

    // createdAt = fuente HTTP (/incidents); serverTime = fallback del feed WS.
    out.push({
      monitorId: monitorId,
      _rank: rankOf(shaped.priority),
      _ms: timeMs(inc.createdAt || inc.serverTime),
      tipo: subtitle || title || 'Incidente',
      categoria: getIncidentCategoryLabel(shaped),
      catIcon: getIncidentMdiIcon(shaped),
      catColor: getIncidentColorVar(shaped),
      lugar: lugar,
      hora: timeAgo(inc.createdAt || inc.serverTime),
      prioridad: shaped.priority,
      prioridadColor: colorOf(shaped.priority),
    })
  }
  out.sort(function(a, b) {
    if (a._rank !== b._rank) return a._rank - b._rank
    return b._ms - a._ms
  })
  return out.slice(0, MAX_TICKER_ITEMS)
}
